import {
  Col,
  ContentItem,
  ContentItems,
  ContentWrapper,
  Flex,
  Row,
  Text,
  TitledText,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { ReviewSheetModel } from '@/features/project/projects/_models/project';
import { FALLBACK_TEXT, ITEM_SPLITTER } from '@/utils';
import { ImplementationMethodLabelModifier } from '@/utils/enum';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { ProjectInformationHeader } from './ProjectInformationHeader';
import { SectionTitle } from './Section';
import { SubsidyRateCard } from './SubsidyRateCard';
import {
  ProjectCategoryPinpointItem,
  ProjectFiscalYearPinpointItem,
  ProjectGroupPinpointItem,
  ProjectLegalBasesPinpointItem,
  ProjectMainCostPinpointItem,
  ProjectNumberPinpointItem,
  ProjectOldProjectNumberPinpointItem,
  ProjectOrderNumberPinpointItem,
  ProjectOverviewPinpointItem,
  ProjectPeriodPinpointItem,
  ProjectPlanPinpointItem,
  ProjectPolicyPinpointItem,
  ProjectPurposePinpointItem,
  ProjectRelatedProjectsPinpointItem,
  ProjectRemarkPinpointItem,
  ProjectSheetTypePinpointItem,
  ProjectTaskPinpointItem,
} from './pinpoint-items/basic-information-pinpoint-fields';

type Props = z.infer<typeof ReviewSheetModel> & {
  historySavedAt: string | undefined;
};

export const RsProjectInformation: FC<Props> = (props) => {
  const {
    id,
    fiscalYear,
    relatedProjects,
    projectNumber,
    oldProjectNumber,
    startYear,
    endYear,
    isUnknownStart,
    isNoScheduledEnd,
    category,
    purpose,
    overview,
    overviewUrl,
    legalBases,
    plans,
    policiesForProject,
    task,
    mainCosts,
    remark,
    sortOrder,
    name,
    sheetType,
    ...rest
  } = ReviewSheetModel.parse(props);
  const { t } = useTranslation();

  return (
    <>
      <ProjectInformationHeader
        ministryId={rest.ministryId}
        historySavedAt={props.historySavedAt}
        organization={rest.organization}
        relatedOrganizations={rest.relatedOrganizations}
        otherResponsibleOrganizations={rest.otherResponsibleOrganizations}
        peopleInCharge={rest.peopleInCharge}
        businessManagers={rest.businessManagers}
        snapshotId={rest.snapshotId}
        pinpointSheetType="RS"
      />
      <ContentWrapper title={t('basic-information')}>
        <ContentItem borderBottom={0}>
          <ContentItems
            items={[
              <ProjectNumberPinpointItem
                key="project-number"
                projectNumber={projectNumber}
                pinpointSheetType={sheetType}
                projectId={id}
              />,
              <ProjectGroupPinpointItem
                key="project-group"
                projectId={id}
                pinpointSheetType={sheetType}
              />,
            ]}
          />
        </ContentItem>
        <Flex vertical gap={4}>
          <ProjectSheetTypePinpointItem
            pinpointSheetType={sheetType}
            projectId={id}
          />
          <Text variant="h4" text={name} />
        </Flex>
        <ContentItem>
          <ContentItems
            mode="fillRight"
            items={[
              <ProjectFiscalYearPinpointItem
                key="fiscal-year"
                projectId={id}
                fiscalYear={fiscalYear}
                pinpointSheetType={sheetType}
              />,
              <ProjectCategoryPinpointItem
                key="project-category"
                projectId={id}
                category={category}
                pinpointSheetType={sheetType}
              />,
              <ProjectPeriodPinpointItem
                key="period"
                projectId={id}
                startYear={startYear}
                isUnknownStart={isUnknownStart}
                endYear={endYear}
                isNoScheduledEnd={isNoScheduledEnd}
                pinpointSheetType={sheetType}
              />,
            ]}
          />
        </ContentItem>

        <ContentItem borderBottom={0} paddingBottom={0}>
          <Flex gap={24} vertical>
            <ProjectPolicyPinpointItem
              projectId={id}
              policiesForProject={policiesForProject}
              pinpointSheetType={sheetType}
            />
          </Flex>
        </ContentItem>

        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <ProjectRelatedProjectsPinpointItem
                key="related-projects"
                projectId={id}
                relatedProjects={relatedProjects}
                pinpointSheetType={sheetType}
              />,
              <ProjectMainCostPinpointItem
                key="main-cost"
                projectId={id}
                cost={mainCosts}
                pinpointSheetType={sheetType}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('overview-and-purpose')}>
        <ContentItem>
          <ContentItems
            items={[
              <ProjectPurposePinpointItem
                key="project-purpose"
                projectId={id}
                purpose={purpose}
                pinpointSheetType={sheetType}
              />,
              <ProjectTaskPinpointItem
                key="project-task"
                projectId={id}
                task={task}
                pinpointSheetType={sheetType}
              />,
            ]}
          />
        </ContentItem>

        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <ProjectOverviewPinpointItem
                key="overview"
                projectId={id}
                pinpointSheetType={sheetType}
                overview={overview}
                overviewUrl={overviewUrl}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('legal-bases')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <Flex vertical gap={24}>
            <ProjectLegalBasesPinpointItem
              projectId={id}
              pinpointSheetType={sheetType}
              legalBases={legalBases ?? []}
            />
            <ProjectPlanPinpointItem
              projectId={id}
              pinpointSheetType={sheetType}
              plans={plans ?? []}
            />
          </Flex>
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper
        title={t('implementation-method-and-subsidy-rate-and-so-on')}
      >
        <ContentItem borderBottom={0} paddingBottom={0}>
          <Flex vertical gap={24}>
            <PinpointMarker
              pinpoint={{
                path: { project: id },
                field: 'rs.implementation-method',
              }}
            >
              <TitledText
                title={t('implementation-method')}
                text={(rest.implementationMethods ?? [])
                  .map((value) => ImplementationMethodLabelModifier[value])
                  .join(ITEM_SPLITTER)}
              />
            </PinpointMarker>
            <Flex vertical gap={8}>
              <PinpointMarker
                pinpoint={{
                  path: { project: id },
                  field: 'rs.subsidy-rate-and-so-on-title',
                }}
              >
                <Text
                  variant="body"
                  text={t('subsidy-rate-and-so-on')}
                  color="secondary"
                />
              </PinpointMarker>
              <Flex vertical gap={24}>
                <Row gutter={[24, 16]}>
                  {rest.subsidyRates?.length ? (
                    rest.subsidyRates.map((subsidyRate, index) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                      <Col span={12} key={index}>
                        <PinpointMarker
                          pinpoint={{
                            path: {
                              project: id,
                              subsidyRate: subsidyRate.target || '',
                            },
                            field: 'rs.subsidy-rate-and-so-on',
                          }}
                        >
                          <SubsidyRateCard subsidyRate={subsidyRate} />
                        </PinpointMarker>
                      </Col>
                    ))
                  ) : (
                    <Col span={12}>
                      <Text text={FALLBACK_TEXT} weight="regular" />
                    </Col>
                  )}
                </Row>
              </Flex>
            </Flex>
          </Flex>
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper borderBottom={0}>
        <SectionTitle title={t('others')} />

        <Row style={{ padding: '32px 0' }} gutter={[24, 0]}>
          <Col span={6}>
            <ProjectOldProjectNumberPinpointItem
              projectId={id}
              oldProjectNumber={oldProjectNumber}
              pinpointSheetType={sheetType}
            />
          </Col>
          <ProjectOrderNumberPinpointItem
            projectId={id}
            sortOrder={sortOrder}
            pinpointSheetType={sheetType}
          />
          <Col span={12}>
            <ProjectRemarkPinpointItem
              projectId={id}
              remark={remark}
              pinpointSheetType={sheetType}
            />
          </Col>
        </Row>
      </ContentWrapper>
    </>
  );
};
